import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { transition } from '../lib/motion.js';
import { useEventStore } from '../store/useEventStore.js';
import { toast } from '../store/useToast.js';
import PreviewButton from './PreviewButton.jsx';
import { REACTION_UI } from '../lib/gamification.js';
import { PRIORITY_FILE_HINT, priorityTier } from '../lib/guestCopy.js';

function rankStyles(rank) {
  if (rank === 1) return { badge: 'bg-accent text-accent-fg' };
  if (rank === 2) return { badge: 'bg-white/20 text-white' };
  if (rank === 3) return { badge: 'bg-white/12 text-white/80' };
  return { badge: 'bg-white/5 text-white/60' };
}

export default function RequestCard({
  request,
  rank,
  passagePercent,
  maxPassagePercent,
  onBoost,
  paidQueueUiEnabled = true,
}) {
  const toggleVoteFor = useEventStore((s) => s.toggleVoteFor);
  const hasVoted = useEventStore((s) => s.votedRequestIds.has(request.id));
  const usernameByBarUserId = useEventStore((s) => s.usernameByBarUserId);
  const reactionCounts = useEventStore((s) => s.reactionCountsByRequest[request.id] ?? {});
  const myReactionKeys = useEventStore((s) => s.myReactionKeysByRequest[request.id] ?? []);
  const toggleRequestReaction = useEventStore((s) => s.toggleRequestReaction);
  const [pending, setPending] = useState(false);
  const [reactionBusy, setReactionBusy] = useState(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const reduceMotion = useReducedMotion();

  const suggestedBy = useMemo(() => {
    const bid = request.bar_user_id;
    if (!bid) return null;
    return usernameByBarUserId[bid] ?? null;
  }, [request.bar_user_id, usernameByBarUserId]);

  const title = request.custom_title ?? 'Titre inconnu';
  const artist = request.custom_artist ?? '—';
  const bidCents = request.bid_amount ?? 0;
  const boostedLabel =
    bidCents <= 0
      ? null
      : `${(bidCents / 100).toLocaleString('fr-FR', {
          minimumFractionDigits: bidCents % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        })} €`;
  const pct = passagePercent ?? 0;
  const priority = priorityTier(pct, maxPassagePercent);
  const styles = rankStyles(rank);
  const isBoosted = paidQueueUiEnabled && (request.is_boosted || bidCents >= 500);
  const cover = request.deezer_cover_url ?? null;
  const canBoost = request.status === 'pending';
  const reactionTotal = Object.values(reactionCounts).reduce((a, b) => a + (Number(b) || 0), 0);

  async function handleVote() {
    if (pending) return;
    setPending(true);
    const res = await toggleVoteFor(request.id);
    setPending(false);
    if (res?.ok) {
      if (res.removed) toast.info('Vote retiré');
      else toast.success('Vote pris en compte');
    } else if (res?.reason === 'already_voted') {
      toast.info('Tu as déjà voté pour cette suggestion.');
    } else if (res) {
      toast.error('Vote impossible. Réessaie.');
    }
  }

  async function handleReaction(key) {
    if (reactionBusy) return;
    setReactionBusy(key);
    const res = await toggleRequestReaction(request.id, key);
    setReactionBusy(null);
    if (res?.ok) return;
    if (res?.reason === 'already_reacted') {
      toast.info('Tu as déjà utilisé cette réaction ici.');
    } else if (res?.reason === 'no_live_event') {
      toast.error('Réactions disponibles seulement pendant la soirée live.');
    } else if (res?.reason === 'not_found' || res?.reason === 'not_mine') {
      return;
    } else if (res) {
      toast.error('Réaction impossible. Réessaie.');
    }
  }

  return (
    <motion.li
      layout={!reduceMotion}
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
      whileHover={reduceMotion ? undefined : { y: -2 }}
      transition={transition(reduceMotion, 'medium')}
      className={`card flex flex-col gap-3 transition-colors ${isBoosted ? 'border-gold/45' : 'hover:border-white/15'}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-mono text-[11px] font-bold tabular-nums ${styles.badge}`}
        >
          {rank}
        </div>

        {cover ? (
          <img
            src={cover}
            alt={`Pochette ${title}`}
            className="h-11 w-11 shrink-0 rounded-lg object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-bg-soft text-accent"
            aria-hidden
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18V5l12-2v13" strokeLinecap="round" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold">{title}</p>
            {isBoosted ? (
              <span className="shrink-0 rounded-full bg-gold/20 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-gold">
                Avant
              </span>
            ) : null}
          </div>
          <p className="truncate text-xs text-white/55">{artist}</p>
          <p className="mt-1 text-[11px] text-white/45">
            <span className="font-mono tabular-nums">{request.votes ?? 0}</span> vote{(request.votes ?? 0) !== 1 ? 's' : ''}
            {request.status === 'play_next' ? (
              <span className="text-emerald-300/90"> · À suivre</span>
            ) : null}
            {suggestedBy ? (
              <span className="text-white/50">
                {' '}
                · par <span className="text-white/75">{suggestedBy}</span>
              </span>
            ) : null}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <PreviewButton
            id={request.id}
            previewUrl={request.deezer_preview_url}
            deezerTrackId={request.deezer_track_id}
            size="md"
          />
          <button
            type="button"
            onClick={handleVote}
            disabled={pending}
            className={`${hasVoted ? 'btn-secondary' : 'btn-primary'} min-h-[44px] px-3 py-2 text-xs`}
            aria-label={hasVoted ? 'Retirer mon vote' : 'Voter pour cette suggestion'}
          >
            {hasVoted ? (pending ? '…' : 'Retirer') : pending ? '…' : 'Voter'}
          </button>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
          <span className="text-white/45" title={PRIORITY_FILE_HINT}>
            Priorité dans la file
          </span>
          <span className="font-medium text-white/80">{priority.label}</span>
        </div>
        <div
          className="relative h-1.5 overflow-hidden rounded-full bg-white/5"
          role="progressbar"
          aria-valuenow={priority.width}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Priorité dans la file : ${priority.label}`}
        >
          <motion.span
            layout
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, Math.max(8, priority.width))}%` }}
            transition={transition(reduceMotion, 'medium')}
            className={`absolute inset-y-0 left-0 rounded-full ${
              isBoosted ? 'bg-gold/80' : 'bg-white/25'
            }`}
          />
        </div>
        {paidQueueUiEnabled && boostedLabel ? (
          <p className="mt-1 text-[11px] text-white/45">
            <span className="font-mono font-semibold tabular-nums text-gold">{boostedLabel}</span> en mise en avant
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-white/5 pt-2.5">
        {paidQueueUiEnabled ? (
          <button
            type="button"
            onClick={() => canBoost && onBoost?.(request)}
            disabled={!canBoost}
            title={
              canBoost
                ? 'Ajouter un montant à cette suggestion'
                : 'Ajout indisponible pour ce statut'
            }
            className={`btn-secondary min-h-[44px] flex-1 py-2 text-xs ${
              !canBoost ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            Soutenir
          </button>
        ) : null}
        <button
          type="button"
          className="btn-ghost min-h-[44px] flex-1 py-2 text-xs text-white/70"
          aria-expanded={reactionsOpen}
          onClick={() => setReactionsOpen((o) => !o)}
        >
          Réagir
          {reactionTotal > 0 ? ` (${reactionTotal})` : ''}
        </button>
      </div>

      {reactionsOpen ? (
        <motion.div
          className="flex flex-wrap gap-1.5"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={transition(reduceMotion, 'fast')}
        >
          {REACTION_UI.map(({ key, emoji, label }) => {
            const count = reactionCounts[key] ?? 0;
            const mine = myReactionKeys.includes(key);
            const busy = reactionBusy === key;
            return (
              <button
                key={key}
                type="button"
                title={mine ? `Retirer : ${label}` : label}
                disabled={Boolean(reactionBusy)}
                onClick={() => void handleReaction(key)}
                className={`inline-flex min-h-[44px] items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition ${
                  mine
                    ? 'border-accent/40 bg-accent/15 text-white'
                    : 'border-border bg-bg-soft text-white/80 hover:bg-white/5'
                } ${busy ? 'opacity-60' : ''}`}
              >
                <span aria-hidden>{emoji}</span>
                <span className="sr-only">{label}</span>
                {count > 0 ? (
                  <span className="tabular-nums text-[10px] font-bold text-white/60">{count}</span>
                ) : null}
              </button>
            );
          })}
        </motion.div>
      ) : null}
    </motion.li>
  );
}
